import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import toast from "react-hot-toast";
import Confirmation from "../modals/confirmation-modal/Confirmation";
import PackageNotesModal from "../modals/package-notes/PackageNotesModal";
import {
  formatOrderDateTime,
  getFinalUpdateSentLabel,
  isOrderFinalUpdateSent,
} from "../../utils/orderFinalUpdate";
import { getApprovedOrderDetailsWeightSummary } from "../modals/order-view-items/orderItemDisplay";
import {
  buildOrderChargesPayload,
  computeDeliveryFromShipping,
  computeGrandTotal,
  formatMoney,
  getItemsSubtotal,
  getOrderChargesFromOrder,
  newExtraChargeRow,
  parseNumericInput,
  resolveDisplayedGrandTotal,
} from "../../utils/orderCharges";
import DownloadInvoiceButton from "./DownloadInvoiceButton";
import {
  StyledAdminInvoiceSection,
  StyledBillAddedChip,
  StyledInlineActionsRow,
  StyledPackageNotesButton,
  StyledPackageNotesLabel,
  StyledPackageNotesPreview,
  StyledPackageNotesRow,
  StyledSummaryHeaderRow,
} from "./style";

const sectionSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1.5,
  px: 1.5,
  py: 1.25,
  mb: 1.5,
  bgcolor: "grey.50",
};

function SummaryRow({ label, value, bold, muted }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        py: 0.35,
        gap: 2,
      }}
    >
      <Typography
        variant="body2"
        color={muted ? "text.disabled" : "text.secondary"}
        fontWeight={bold ? 700 : 400}
        sx={{ flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 700 : 500}
        color={bold ? "text.primary" : "text.secondary"}
        sx={{ textAlign: "right" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function formatShippingWeightDisplay(value) {
  const t = String(value ?? "").trim();
  if (!t || t === "0") return "—";
  const n = Number(t);
  return Number.isFinite(n) ? `${t} kg` : t;
}

function formatShippingRateDisplay(value) {
  const t = String(value ?? "").trim();
  if (!t || t === "0") return "—";
  const n = Number(t);
  return Number.isFinite(n) ? formatMoney(n) : t;
}

function OpenTextChargeField({
  label,
  value,
  onChange,
  onBlur,
  editable,
  placeholder,
  formatDisplay,
  currencyPrefix = false,
  inputWidth,
}) {
  if (!editable) {
    return <SummaryRow label={label} value={formatDisplay(value)} />;
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        py: 0.4,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
        placeholder={currencyPrefix ? undefined : placeholder}
        inputProps={{ inputMode: "decimal" }}
        InputProps={
          currencyPrefix
            ? {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    ₹
                  </InputAdornment>
                ),
              }
            : undefined
        }
        sx={{ width: inputWidth ?? (currencyPrefix ? 132 : 120), flexShrink: 0 }}
      />
    </Box>
  );
}

function ChargeField({ label, value, onChange, editable, currencyPrefix = false }) {
  if (!editable) {
    const n = Number(value);
    const display = !Number.isFinite(n) || n === 0 ? formatMoney(0) : formatMoney(n);
    return <SummaryRow label={label} value={display} />;
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        py: 0.4,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <TextField
        size="small"
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ min: 0, step: "0.01" }}
        InputProps={
          currencyPrefix
            ? {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    ₹
                  </InputAdornment>
                ),
              }
            : undefined
        }
        sx={{ width: currencyPrefix ? 132 : 120, flexShrink: 0 }}
      />
    </Box>
  );
}

function FinalBillBadge({ sentAtLabel, compact }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        mb: compact ? 1.5 : 2,
        py: 1,
        px: 1.25,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "success.main",
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(46, 125, 50, 0.12)" : "rgba(46, 125, 50, 0.08)",
      }}
    >
      <CheckCircleOutlineIcon color="success" sx={{ fontSize: 20, mt: 0.15 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} color="success.dark">
          Final Bill Added
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Sent to customer{sentAtLabel ? ` · ${sentAtLabel}` : ""}
        </Typography>
      </Box>
    </Box>
  );
}

export default function OrderSummaryCharges({
  order,
  editable = false,
  adminView = false,
  saving = false,
  onSave,
  onFinalUpdate,
  finalUpdateLoading = false,
  recipientName,
  onSavePackageNotes,
  packageNotesSaving = false,
}) {
  const [confirmFinalOpen, setConfirmFinalOpen] = useState(false);
  const [packageNotesOpen, setPackageNotesOpen] = useState(false);
  const [invoiceId, setInvoiceId] = useState("");
  const finalUpdateSent = isOrderFinalUpdateSent(order);
  const chargesEditable = editable && !finalUpdateSent;
  const packageNotesEditable =
    adminView &&
    !finalUpdateSent &&
    !["cancelled", "rejected"].includes(String(order?.order_status || "").toLowerCase());
  const packageNotesText =
    order?.package_notes != null ? String(order.package_notes) : "";
  const finalUpdateSentLabel = getFinalUpdateSentLabel(order);

  useEffect(() => {
    if (finalUpdateSent) setPackageNotesOpen(false);
  }, [finalUpdateSent]);

  const orderCreatedLabel = formatOrderDateTime(order?.createdAt);
  const itemCount = useMemo(
    () => (order?.order_details || []).filter(
      (d) => String(d?.item?.status ?? d?.status ?? "").toLowerCase() === "approved"
    ).length,
    [order]
  );
  const itemsSubtotal = useMemo(() => getItemsSubtotal(order), [order]);
  const itemsWeightSummary = useMemo(
    () => getApprovedOrderDetailsWeightSummary(order?.order_details || []),
    [order]
  );
  const itemsTotalWeight = itemsWeightSummary.totalWeight || 0;
  const itemsTotalWeightLabel = itemsWeightSummary.totalWeightLabel || "—";

  const [totalShippingWeight, setTotalShippingWeight] = useState("");
  const [shippingCostPerKg, setShippingCostPerKg] = useState("");
  const [deliveryCharge, setDeliveryCharge] = useState("0");
  const [tax, setTax] = useState("0");
  const [extraCharges, setExtraCharges] = useState([]);

  const syncDeliveryFromShipping = (weightText, rateText) => {
    const computed = computeDeliveryFromShipping(weightText, rateText);
    if (computed != null) setDeliveryCharge(String(computed));
  };

  useEffect(() => {
    setInvoiceId(order?.invoice_id != null ? String(order.invoice_id) : "");
  }, [order?.invoice_id, order?.id]);

  useEffect(() => {
    const parsed = getOrderChargesFromOrder(order);
    // Use already-saved weight if present, else fall back to approved items total weight.
    const savedWeight = parseNumericInput(parsed.totalShippingWeight);
    const nextWeight =
      Number.isFinite(savedWeight) && savedWeight > 0
        ? String(parsed.totalShippingWeight)
        : itemsTotalWeight > 0
          ? String(Number(itemsTotalWeight.toFixed(2)))
          : "0";

    setTotalShippingWeight(nextWeight);
    setShippingCostPerKg(parsed.shippingCostPerKg ?? "");
    setTax(String(parsed.tax ?? 0));
    setExtraCharges(parsed.extraCharges.length > 0 ? parsed.extraCharges : []);

    const rate = parsed.shippingCostPerKg ?? "";
    const computed = computeDeliveryFromShipping(nextWeight, rate);
    setDeliveryCharge(
      computed != null ? String(computed) : String(parsed.deliveryCharge ?? 0)
    );
  }, [order, itemsTotalWeight]);

  const numericDelivery = Number(deliveryCharge) || 0;
  const numericTax = Number(tax) || 0;
  const totalAdditionalCharges = useMemo(
    () =>
      (extraCharges || []).reduce(
        (sum, row) => sum + (Number.isFinite(Number(row.amount)) ? Number(row.amount) : 0),
        0
      ),
    [extraCharges]
  );

  const computedGrandTotal = useMemo(
    () =>
      computeGrandTotal({
        deliveryCharge: numericDelivery,
        marketplaceFee: 0,
        tax: numericTax,
        extraCharges,
      }),
    [numericDelivery, numericTax, extraCharges]
  );

  const grandTotal = chargesEditable
    ? computedGrandTotal
    : resolveDisplayedGrandTotal(order, computedGrandTotal);

  const totalBeforeTax = numericDelivery;

  const taxTrim = String(tax ?? "").trim();
  const taxNum = parseNumericInput(tax);
  const hasValidTax = Boolean(taxTrim) && Number.isFinite(taxNum) && taxNum > 0;

  const rateTrimValue = String(shippingCostPerKg ?? "").trim();
  const rateNumValue = parseNumericInput(shippingCostPerKg);
  const hasValidCostPerKg =
    Boolean(rateTrimValue) && Number.isFinite(rateNumValue) && rateNumValue > 0;

  const weightTrimValue = String(totalShippingWeight ?? "").trim();
  const weightNumValue = parseNumericInput(totalShippingWeight);
  const hasValidShippingWeight =
    Boolean(weightTrimValue) &&
    Number.isFinite(weightNumValue) &&
    weightNumValue > 0 &&
    !(itemsTotalWeight > 0 && weightNumValue < itemsTotalWeight);

  const finalBillBlockReason = !hasValidShippingWeight
    ? itemsTotalWeight > 0 &&
      Number.isFinite(weightNumValue) &&
      weightNumValue < itemsTotalWeight
      ? `Total KG must be at least ${itemsTotalWeightLabel} (approved items) to send the final bill.`
      : "Enter a valid Total KG to send the final bill."
    : !hasValidCostPerKg
      ? "Enter a Cost per kg greater than 0 to send the final bill."
      : !hasValidTax
        ? "Enter a Tax Amount greater than 0 to send the final bill."
        : null;

  const canSendFinalBill = !finalBillBlockReason;

  const validateTaxAmount = () => {
    if (!taxTrim) {
      toast.error("Tax Amount is required.");
      return false;
    }
    if (!Number.isFinite(taxNum) || taxNum <= 0) {
      toast.error("Tax Amount must be greater than 0.");
      return false;
    }
    return true;
  };

  const validateCostPerKg = () => {
    if (!rateTrimValue) {
      toast.error("Cost per kg is required.");
      return false;
    }
    if (!Number.isFinite(rateNumValue) || rateNumValue <= 0) {
      toast.error("Cost per kg must be greater than 0.");
      return false;
    }
    return true;
  };

  const validateShippingWeight = () => {
    const weightTrim = String(totalShippingWeight).trim();
    const weightNum = parseNumericInput(totalShippingWeight);

    if (!weightTrim) {
      toast.error("Total KG is required.");
      return false;
    }
    if (!Number.isFinite(weightNum)) {
      toast.error("Total Shipping Weight must be a valid number.");
      return false;
    }
    if (weightNum < 0) {
      toast.error("Total Shipping Weight cannot be negative.");
      return false;
    }
    if (itemsTotalWeight > 0 && weightNum < itemsTotalWeight) {
      toast.error(
        `Total KG cannot be less than approved items total weight (${itemsTotalWeightLabel}).`
      );
      return false;
    }
    return true;
  };

  const getSavePayload = () => {
    const weightTrim = String(totalShippingWeight).trim();
    const rateTrim = String(shippingCostPerKg).trim();
    const weightNum = parseNumericInput(totalShippingWeight);
    const rateNum = parseNumericInput(shippingCostPerKg);

    return buildOrderChargesPayload({
      totalShippingWeight: weightTrim ? weightNum : 0,
      shippingCostPerKg: rateTrim ? rateNum : 0,
      deliveryCharge: numericDelivery,
      marketplaceFee: 0,
      tax: numericTax,
      extraCharges,
      invoiceId,
    });
  };

  const handleSave = () => {
    if (!onSave || !chargesEditable) return;

    if (!validateShippingWeight()) return;
    if (!validateCostPerKg()) return;
    if (!validateTaxAmount()) return;

    onSave(getSavePayload());
  };

  const handleRequestFinalUpdate = () => {
    if (!validateShippingWeight()) return;
    if (!validateCostPerKg()) return;
    if (!validateTaxAmount()) return;
    setConfirmFinalOpen(true);
  };

  const updateExtra = (id, field, value) => {
    setExtraCharges((rows) =>
      rows.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const removeExtra = (id) => {
    setExtraCharges((rows) => rows.filter((r) => r.id !== id));
  };

  return (
    <Box>
      <StyledSummaryHeaderRow>
        <Typography fontWeight={700}>Order summary</Typography>
        <StyledInlineActionsRow>
          {finalUpdateSent && (
            <StyledBillAddedChip
              size="small"
              icon={<CheckCircleOutlineIcon />}
              label="Bill Added"
              color="success"
              variant="outlined"
            />
          )}
          <DownloadInvoiceButton order={order} recipientName={recipientName} isAdmin={adminView} />
        </StyledInlineActionsRow>
      </StyledSummaryHeaderRow>

      <Typography variant="caption" color="text.secondary" display="block">
        {order?.order_number ? `Order # ${order.order_number}` : `Order ${order?.id || "—"}`}
        {orderCreatedLabel && ` · ${orderCreatedLabel}`}
      </Typography>

      {adminView && (
        <StyledAdminInvoiceSection>
          <OpenTextChargeField
            label="Invoice ID"
            value={invoiceId}
            onChange={setInvoiceId}
            editable={!finalUpdateSent}
            placeholder="Enter invoice ID"
            formatDisplay={(value) => (String(value || "").trim() ? String(value).trim() : "—")}
            inputWidth={220}
          />
          <StyledPackageNotesRow>
            <StyledPackageNotesLabel variant="body2">
              Package notes
            </StyledPackageNotesLabel>
            {packageNotesEditable ? (
              <StyledPackageNotesButton
                size="small"
                variant="outlined"
                onClick={() => setPackageNotesOpen(true)}
              >
                {packageNotesText.trim() ? "Edit package notes" : "Add package notes"}
              </StyledPackageNotesButton>
            ) : null}
          </StyledPackageNotesRow>
          {packageNotesText.trim() ? (
            <StyledPackageNotesPreview variant="caption">
              {packageNotesText.trim()}
            </StyledPackageNotesPreview>
          ) : null}
        </StyledAdminInvoiceSection>
      )}
      {!chargesEditable && finalUpdateSent && (
        <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
          Approved bill sent{finalUpdateSentLabel ? ` · ${finalUpdateSentLabel}` : ""}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <SummaryRow label={`Items (${itemCount})`} value={String(itemCount)} />

        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ display: "block", mt: 1.5, mb: 0.75, textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          Shipping
        </Typography>
        <Box sx={chargesEditable ? sectionSx : { mb: 0.5 }}>
          <OpenTextChargeField
            label={adminView ? "Total KG" : "Total shipping weight"}
            value={totalShippingWeight}
            onChange={(v) => {
              setTotalShippingWeight(v);
              if (chargesEditable) syncDeliveryFromShipping(v, shippingCostPerKg);
            }}
            onBlur={(v) => {
              if (itemsTotalWeight > 0) {
                const num = parseNumericInput(v);
                if (Number.isFinite(num) && num < itemsTotalWeight) {
                  const clamped = String(Number(itemsTotalWeight.toFixed(2)));
                  setTotalShippingWeight(clamped);
                  if (chargesEditable) syncDeliveryFromShipping(clamped, shippingCostPerKg);
                }
              }
            }}
            editable={chargesEditable}
            placeholder="kg"
            formatDisplay={formatShippingWeightDisplay}
            inputWidth={adminView ? 132 : undefined}
          />
          <OpenTextChargeField
            label="Cost per kg"
            value={shippingCostPerKg}
            onChange={(v) => {
              setShippingCostPerKg(v);
              if (chargesEditable) syncDeliveryFromShipping(totalShippingWeight, v);
            }}
            editable={chargesEditable}
            placeholder="₹"
            formatDisplay={formatShippingRateDisplay}
            currencyPrefix={adminView}
          />
          {/* {!adminView && (
            <>
              <ChargeField
                label="Delivery charge"
                value={deliveryCharge}
                onChange={setDeliveryCharge}
                editable={chargesEditable}
                currencyPrefix
              />
              {chargesEditable &&
                String(totalShippingWeight).trim() &&
                String(shippingCostPerKg).trim() &&
                computeDeliveryFromShipping(totalShippingWeight, shippingCostPerKg) != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    {totalShippingWeight.trim()} × {shippingCostPerKg.trim()} ={" "}
                    {formatMoney(computeDeliveryFromShipping(totalShippingWeight, shippingCostPerKg))}
                  </Typography>
                )}
            </>
          )} */}
        </Box>

        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ display: "block", mt: 1, mb: 0.75, textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          Fees & tax
        </Typography>

        {!chargesEditable && extraCharges.length > 0 && (
          <Box sx={{ mt: 0.5, mb: 0.5 }}>
            {extraCharges.map((row) => (
              <SummaryRow
                key={row.id}
                label={row.label || "Additional charge"}
                value={formatMoney(row.amount)}
              />
            ))}
          </Box>
        )}

        {chargesEditable && (
          <Box sx={{ ...sectionSx, mt: 0, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Additional charges
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setExtraCharges((rows) => [...rows, newExtraChargeRow()])}
                sx={{ textTransform: "none", minWidth: 0, py: 0.25 }}
              >
                Add
              </Button>
            </Box>
            {extraCharges.length === 0 ? (
              <Typography variant="caption" color="text.disabled">
                Optional — handling, customs, etc.
              </Typography>
            ) : (
              extraCharges.map((row) => (
                <Box key={row.id} sx={{ display: "flex", gap: 0.75, mb: 0.75, alignItems: "center" }}>
                  <TextField
                    size="small"
                    placeholder="Label"
                    value={row.label}
                    onChange={(e) => updateExtra(row.id, "label", e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={row.amount}
                    onChange={(e) => updateExtra(row.id, "amount", e.target.value)}
                    inputProps={{ min: 0, step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ mr: 0 }}>
                          ₹
                        </InputAdornment>
                      ),
                    }}
                    sx={{ width: 100 }}
                  />
                  <IconButton size="small" onClick={() => removeExtra(row.id)} aria-label="Remove">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Box>
        )}

        <ChargeField
          label="Estimated tax"
          value={tax}
          onChange={setTax}
          editable={chargesEditable}
          currencyPrefix={adminView}
        />
      <Divider sx={{ my: 1.5 }} />

        <Box sx={{ mt: 1.25 }}>
          <SummaryRow label="Subtotal before tax" value={formatMoney(totalBeforeTax)} muted />
        </Box>
      </Box>


      <Box sx={{ mb: 0.25 }}>
        <SummaryRow label="Additional charges" value={formatMoney(totalAdditionalCharges)} />
        <SummaryRow label="Tax" value={formatMoney(numericTax)} />
      </Box>
      <Divider sx={{ my: 1.25 }} />
      <SummaryRow label="Grand total" value={formatMoney(grandTotal)} bold />

      {editable && (
        <>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
            Totals update as you edit charges.
          </Typography>
          {onFinalUpdate && finalUpdateSent && (
            <FinalBillBadge sentAtLabel={finalUpdateSentLabel} compact />
          )}
          {!finalUpdateSent && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mt: 2,
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <Button
                variant="contained"
                sx={{ flex: 1, minWidth: 0, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                onClick={handleSave}
                disabled={saving || finalUpdateLoading || !canSendFinalBill}
              >
                {saving ? "Saving…" : "Save charges"}
              </Button>
              {onFinalUpdate && (
                <Button
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 0, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
                  onClick={handleRequestFinalUpdate}
                  disabled={finalUpdateLoading || saving || !canSendFinalBill}
                >
                  {finalUpdateLoading ? "Sending…" : "Approve & send final bill"}
                </Button>
              )}
            </Box>
          )}
          {onFinalUpdate && !finalUpdateSent && (
            <Typography
              variant="caption"
              color={finalBillBlockReason ? "error" : "text.secondary"}
              display="block"
              sx={{ mt: 0.75, textAlign: "center" }}
            >
              {finalBillBlockReason ||
                "Save charges first, then send the approved bill to the customer."}
            </Typography>
          )}
          {onFinalUpdate && (
            <Confirmation
              open={confirmFinalOpen}
              handleClose={() => setConfirmFinalOpen(false)}
              heading="Approve final bill"
              message="Send the current order total and charges to the customer? They will see this as the approved final bill."
              confirmButtonLabel="Send to customer"
              type="info"
              onConfirm={() => {
                if (!validateShippingWeight()) return;
                if (!validateCostPerKg()) return;
                if (!validateTaxAmount()) return;
                setConfirmFinalOpen(false);
                if (onSave) {
                  onSave(getSavePayload(), () => {
                    onFinalUpdate(invoiceId);
                  });
                } else {
                  onFinalUpdate(invoiceId);
                }
              }}
            />
          )}
        </>
      )}

      {adminView && (
        <PackageNotesModal
          open={packageNotesOpen && packageNotesEditable}
          handleClose={() => setPackageNotesOpen(false)}
          initialNotes={packageNotesText}
          readOnly={!packageNotesEditable}
          saving={packageNotesSaving}
          onSave={(notes) => {
            if (!onSavePackageNotes) return;
            onSavePackageNotes(notes, () => setPackageNotesOpen(false));
          }}
        />
      )}
    </Box>
  );
}
